import { useState } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success("Reset link sent if the email is on file.");
    } catch (err) {
      // Surface only generic message to avoid enumeration
      toast.error(err.response?.data?.detail || "Could not send reset link. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-mm-parchment bg-topo flex flex-col" data-testid="forgot-password-page">
      <div className="container mx-auto py-6">
        <Link to="/"><Logo size={44} /></Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white border border-mm-navy/10 rounded-2xl p-8 fade-up">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">Trail rescue</div>
          <h1 className="font-heading text-3xl font-black text-mm-navy mt-2">Forgot password?</h1>
          <p className="text-mm-navy/70 mt-2 text-sm">
            No worries — every climber loses the path sometimes. Enter your email and we'll send a reset link.
          </p>

          {sent ? (
            <div className="mt-6 rounded-lg bg-mm-parchment/60 border border-mm-navy/10 p-4" data-testid="forgot-password-sent-state">
              <div className="font-bold text-mm-navy">Check your inbox</div>
              <p className="text-sm text-mm-navy/70 mt-1">
                If an account exists for <span className="font-semibold">{email}</span>, a password reset link is on its way.
                The link expires in 60 minutes.
              </p>
              <div className="mt-4 flex gap-3">
                <Link to="/login" className="btn-trail btn-gold" data-testid="forgot-password-back-to-login-btn">
                  Back to login
                </Link>
                <button
                  type="button"
                  className="text-sm font-bold text-mm-navy hover:text-mm-steel"
                  onClick={() => { setSent(false); }}
                  data-testid="forgot-password-resend-btn"
                >
                  Use a different email
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-mm-steel">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1.5 w-full bg-white border-2 border-mm-navy/15 rounded-lg px-4 py-3 focus:outline-none focus:border-mm-steel focus:ring-2 focus:ring-mm-steel/30"
                  data-testid="forgot-password-email-input"
                />
              </div>
              <button type="submit" disabled={loading} className="btn-trail btn-gold w-full mt-2" data-testid="forgot-password-submit-btn">
                {loading ? "Sending rescue beacon..." : "Send reset link"}
              </button>
            </form>
          )}

          <div className="text-sm text-mm-navy/70 mt-6 text-center">
            Remembered it?{" "}
            <Link to="/login" className="font-bold text-mm-navy hover:text-mm-steel" data-testid="forgot-password-to-login-link">
              Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
